import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Icon, IconsEnum } from 'components/Icons';
import { SportsFavoritesMenuManifestazioneDto, SportsVoceMenu } from 'types/swagger';
import { Root as Accordion } from '@radix-ui/react-accordion';
import { Spinner } from 'components/spinner';
import { SportNavAccordion } from 'features/sport/components/sportNav/sportNavAccordion/SportNavAccordion';
import classNames from 'classnames';
import { isTruthy } from 'utility/functions';
import styles from './SportNavAccordionList.module.scss';
import { useGetLabelsByKeys } from 'hooks/useLingUI';
import { useInternalSession, useVirtualDom } from 'hooks';
import { SkeletonArea } from 'components/skeletonArea';
import useWindowSize from 'hooks/useWindowSize';
import { isClientSide } from 'utility/constant';
import { useTypedSelector } from 'lib/centralStore';
import { selectMenuManifestazioni } from 'features/sport/selectors';

export type SportNavAccordionListProps = {
  gruppi: Array<SportsVoceMenu>;
  manifestazioniPreferitiList: SportsFavoritesMenuManifestazioneDto[];
};

export const SportNavAccordionList = ({ manifestazioniPreferitiList, gruppi }: SportNavAccordionListProps) => {
  const { isLoading } = useTypedSelector(selectMenuManifestazioni);

  const [labelManifestazioniPreferiti, labelManifestazioniNotFound] = useGetLabelsByKeys([
    'le-mie-manifestazioni',
    'manifestazioni-not-found',
  ]);
  const { isAuthenticated } = useInternalSession();
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const { isMobile } = useWindowSize();

  useEffect(() => {
    if (isClientSide) {
      const savedAccordion = sessionStorage.getItem('openSportAccordion');
      if (savedAccordion) {
        setOpenAccordion(savedAccordion);
      }
    }
  }, []);

  const handleAccordionChange = useCallback((value: string) => {
    if (isClientSide) {
      sessionStorage.setItem('openSportAccordion', value);
    }
    setOpenAccordion(value);
  }, []);

  const allValidIds = useMemo(
    (): Array<string> =>
      (gruppi ?? []).reduce((acc, gruppo) => {
        if (isTruthy(gruppo?.manifestazioni?.length)) {
          acc.push(`${gruppo.id}`);
        }
        return acc;
      }, Array<string>()),
    [gruppi]
  );

  const { keys, heights, wrapperRef } = useVirtualDom<HTMLDivElement>({ uids: allValidIds, counter: 20 });

  const renderContent = useCallback((): JSX.Element => {
    const key = 'SportNavAccordionList-Content';

    if (gruppi?.some((gruppo) => isTruthy(gruppo?.manifestazioni?.length))) {
      return (
        <React.Fragment key={`Accordion-${key}`}>
          {gruppi
            .filter((x) => isTruthy(x?.manifestazioni?.length))
            .map((gruppo, idx) => {
              const { id, descrizione, descrizioneTrKey, iconSrc, manifestazioni } = gruppo ?? {};
              if (isMobile || keys?.includes(`${id}`)) {
                return (
                  <SportNavAccordion
                    key={`${id}-${idx}`}
                    uid={`${id}`}
                    iconSrc={iconSrc}
                    descrizione={descrizione}
                    descrizioneTrKey={descrizioneTrKey}
                    manifestazioni={manifestazioni ?? []}
                  />
                );
              }
              return (
                <SkeletonArea
                  height="4"
                  key={`${id}-${idx}`}
                  className={styles.loader}
                  uid={`${id}`}
                  style={{ height: heights[id!], margin: 0 }}
                />
              );
            })}
        </React.Fragment>
      );
    }

    // n.b.: loading just to prevent "invalid filters" message when navigating from top-sport page
    if (isLoading) {
      return <Spinner className={classNames(styles.containerWithoutGruppi, styles.spinner)} key={`Spinner-${key}`} />;
    }

    return (
      <div className={styles.containerWithoutGruppi} key={`Message-${key}`}>
        <Icon iconId={IconsEnum.SEARCH_OFF} className={styles.icon} />
        {labelManifestazioniNotFound}
      </div>
    );
  }, [keys, gruppi, heights, isLoading, isMobile, labelManifestazioniNotFound]);

  if (isLoading && !isTruthy(manifestazioniPreferitiList.length)) {
    return <Spinner className={classNames(styles.containerWithoutGruppi, styles.spinner)} key={`general-spinner`} />;
  }

  return (
    <Accordion
      ref={wrapperRef}
      type="single"
      collapsible
      className={styles.container}
      defaultValue={openAccordion ?? ''}
      onValueChange={handleAccordionChange}
    >
      {isAuthenticated && manifestazioniPreferitiList && (
        <SportNavAccordion
          descrizione={`${labelManifestazioniPreferiti} (${manifestazioniPreferitiList.length})`}
          manifestazioni={manifestazioniPreferitiList.map((item) => ({ ...item, isPreferito: true }))}
        />
      )}
      {renderContent()}
    </Accordion>
  );
};